import React from 'react';
import clsx from 'clsx';
import {Swiper, SwiperSlide} from 'swiper/react';
import {useMatches} from '@remix-run/react';
import type {
  Product,
  MediaEdge,
  ExternalVideo,
  MediaImage,
  Model3d,
  Video,
  ProductVariant,
} from '@shopify/hydrogen/storefront-api-types';
import {ATTR_LOADING_EAGER} from '~/lib/const';
import {MediaFile} from '@shopify/hydrogen';
import {Swiper as SwiperClass} from 'swiper';
import {isDiscounted, isNewArrival} from '~/lib/utils';
import type {MoneyV2} from '@shopify/hydrogen/storefront-api-types';

//styles
import 'swiper/swiper.min.css';

//domains
import {
  isStaffPick,
  SanityProductSettings,
  specialProductTags,
} from 'domains/product';

//components
import ProductLabel from 'components/product/ProductLabel';

export default function ProductDetailsCarousel({
  selectedVariant,
}: {
  selectedVariant: ProductVariant;
}) {
  const matches = useMatches();
  const routeData = matches.find(
    (m) => m.id === 'routes/($lang)/products/$productHandle',
  )?.data;
  const product = routeData.product as Product & {
    selectedVariant?: ProductVariant;
  };
  const carouselMainRef = React.useRef<SwiperClass>();
  const carouselNavRef = React.useRef<SwiperClass>();

  if (!product.media || !product.media.nodes) {
    return <></>;
  }

  const allMediaUrl = product.media.nodes.map((m) => {
    if (m.__typename === 'MediaImage') {
      return m?.image?.url;
    }
    return null;
  });
  const selectedVariantMedia = allMediaUrl.find((mediaUrl) => {
    return mediaUrl === selectedVariant?.image?.url;
  });

  const selectedVariantMediaIndex = allMediaUrl?.indexOf(selectedVariantMedia);

  React.useEffect(() => {
    if (carouselMainRef?.current) {
      carouselMainRef.current.slideTo(selectedVariantMediaIndex);
    }
  }, [selectedVariantMediaIndex]);

  const topLabel =
    isDiscounted(
      selectedVariant.price as MoneyV2,
      selectedVariant.compareAtPrice as MoneyV2,
    ) &&
    selectedVariant.compareAtPrice &&
    `-${Math.ceil(
      100 -
        (parseFloat(selectedVariant.price.amount) /
          parseFloat(selectedVariant.compareAtPrice.amount)) *
          100,
    )}%`;
  const tags = [
    ...product?.tags?.filter((t) => specialProductTags.includes(t)),
    selectedVariant.availableForSale ? 'instock' : 'outofstock',
  ];

  return (
    <div className="mb-8 lg:col-span-7 lg:mb-0">
      <div className="grid grid-cols-5">
        <div className="relative col-span-1 hidden h-full overflow-hidden lg:block">
          <div className="absolute top-0 left-0 h-full w-full">
            {/* vertical carousel nav */}
            <Swiper
              onSwiper={(swiper) => {
                carouselNavRef.current = swiper;
              }}
              slidesPerView={4}
              direction={'vertical'}
              className="absolute top-0 left-0 h-full w-full"
            >
              {product.media.nodes.map((med, i) => {
                return (
                  <SwiperSlide key={`carousel-nav-desktop-${i}`}>
                    <button
                      onClick={() => {
                        if (carouselMainRef?.current) {
                          carouselMainRef.current.slideTo(i);
                        }
                      }}
                    >
                      <CarouselMedia med={med} i={i} />
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
        <div className="col-span-5 lg:col-span-4">
          {/* main carousel */}
          <div className="relative">
            <Swiper
              slidesPerView={1}
              onSwiper={(swiper) => {
                carouselMainRef.current = swiper;
              }}
              onSlideChange={(swiper) => {
                const nextSlide = swiper.activeIndex;
                if (carouselNavRef?.current) {
                  carouselNavRef.current.slideTo(nextSlide);
                }
              }}
            >
              {product.media.nodes.map((med, i) => {
                if (med.__typename === 'MediaImage') {
                  return (
                    <SwiperSlide key={`main-carousel-${i}`}>
                      <CarouselMainSlide imageUrl={med.image?.url ?? ''}>
                        <CarouselMedia med={med} i={i} />
                      </CarouselMainSlide>
                    </SwiperSlide>
                  );
                }
                return (
                  <SwiperSlide key={`main-carousel-${i}`}>
                    <CarouselMedia med={med} i={i} />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="absolute top-0 right-0 min-w-[50%]">
              {topLabel && <ProductLabel label={topLabel} tag="discount" />}
              {tags &&
                tags.map(
                  (tag) =>
                    tag && (
                      <ProductLabel
                        key={`product-tag-${tag}`}
                        tag={tag as string}
                      />
                    ),
                )}
            </div>
          </div>
        </div>
        <div className="col-span-5 lg:hidden">
          {/* mobile carousel nav */}
          <Swiper
            onSwiper={(swiper) => {
              carouselNavRef.current = swiper;
            }}
            slidesPerView={4}
            className="absolute top-0 left-0 h-full w-full"
          >
            {product.media.nodes.map((med, i) => {
              return (
                <SwiperSlide key={`carousel-nav-desktop-${i}`}>
                  <button
                    onClick={() => {
                      if (carouselMainRef?.current) {
                        carouselMainRef.current.slideTo(i);
                      }
                    }}
                  >
                    <CarouselMedia med={med} i={i} />
                  </button>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </div>
  );
}

function CarouselMainSlide({
  imageUrl,
  children,
}: {
  imageUrl: string;
  children: React.ReactNode;
}) {
  const [zoom, setZoom] = React.useState({x: 50, y: 50});
  const [isZoomed, setIsZoomed] = React.useState(false);
  const handleMouseMove = (e: React.MouseEvent) => {
    const eventTarget = e.target as HTMLElement;
    const {left, top, width, height} = eventTarget.getBoundingClientRect();
    const x = ((e.pageX - left) / width) * 100;
    const y = ((e.pageY - top) / height) * 100;
    setZoom({x, y});
  };
  return (
    <div
      className="relative"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsZoomed(true)}
      onMouseLeave={() => setIsZoomed(false)}
    >
      <div
        className={clsx(
          'absolute top-1/2 left-1/2 z-10 h-full w-full -translate-x-1/2 -translate-y-1/2 scale-125 bg-no-repeat duration-100 ease-out transition-opacity',
          isZoomed ? 'opacity-100' : 'opacity-0',
        )}
        style={{
          backgroundImage: `url(${imageUrl})`,
          backgroundPositionX: `${zoom.x}%`,
          backgroundPositionY: `${zoom.y}%`,
        }}
      ></div>
      {children}
    </div>
  );
}

function CarouselMedia({
  med,
  i,
}: {
  med: ExternalVideo | MediaImage | Model3d | Video;
  i: number;
}) {
  let mediaProps: Record<string, any> = {};
  const isFirst = i === 0;
  const isFourth = i === 3;
  const isFullWidth = i % 3 === 0;

  const data = {
    ...med,
    image: {
      // @ts-ignore
      ...med.image,
      altText: med.alt || 'Product image',
    },
  } as MediaImage;

  switch (med.mediaContentType) {
    case 'IMAGE':
      mediaProps = {
        width: 800,
        widths: [400, 800, 1200, 1600, 2000, 2400],
      };
      break;
    case 'VIDEO':
      mediaProps = {
        width: '100%',
        autoPlay: true,
        controls: false,
        muted: true,
        loop: true,
        preload: 'auto',
      };
      break;
    case 'EXTERNAL_VIDEO':
      mediaProps = {width: '100%'};
      break;
    case 'MODEL_3D':
      mediaProps = {
        width: '100%',
        interactionPromptThreshold: '0',
        ar: true,
        loading: ATTR_LOADING_EAGER,
        disableZoom: true,
      };
      break;
  }

  if (i === 0 && med.mediaContentType === 'IMAGE') {
    mediaProps.loading = ATTR_LOADING_EAGER;
  }
  if ((med as MediaImage).image) {
    return (
      <MediaFile
        tabIndex={i}
        className={`w-full h-full aspect-square fadeIn object-cover`}
        data={data}
        // @ts-ignore
        // options={{
        //   crop: 'center',
        //   scale: 2,
        // }}
        {...mediaProps}
      />
    );
  }
  return <></>;
}
